//@ts-check
import { useState, useRef, useEffect } from 'react';
import { FormControl, Select, MenuItem, useTheme } from '@mui/material';
import { moveGroupMemberViaAdmin } from '../../repository/OrganizationDAO';
import { useToast } from '../../context_providers/toast_context/ToastContext';
import { useTranslation } from 'react-i18next';
import {
  StringKeys,
  SuccessStringKeys,
  ErrorStringKeys,
} from '../../resources/strings/StringKeys';
import LoadingData from '../loading/LoadingData';
import COLORS from '@/resources/style/Themes';

const DEFAULT_SELECTOR_FONT_SIZE = 15;

/**
 * @typedef {object} GroupSelectorProps
 * @prop {import('@/models/Organization/Group').default[]} allGroups
 * @prop {string} currentGroup
 * @prop {import('@/models/User/OrgUser').OrgUser} user
 * @prop {string} orgAzureId
 * @prop {string} orgCode
 * @prop {(auth_id: string) => void} userToHighlight
 * @prop {number} [fontSize]
 */

/**
 *
 * @param {GroupSelectorProps} props
 * @returns
 */
function GroupSelector({
  allGroups,
  currentGroup,
  user,
  orgAzureId,
  orgCode,
  userToHighlight = () => {},
  fontSize = DEFAULT_SELECTOR_FONT_SIZE,
}) {
  const [loading, setLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(currentGroup);

  const latestSelectionRef = useRef(currentGroup);
  const { showSuccessToast, showErrorToast } = useToast();
  const { t } = useTranslation();
  const { palette } = useTheme();

  useEffect(() => {
    setSelectedGroup(currentGroup);
    latestSelectionRef.current = currentGroup;
  }, [user?.auth_id, currentGroup]);

  /**
   *
   * @param {string} newGroup
   */
  const updateUserGroup = async (newGroup) => {
    latestSelectionRef.current = newGroup; // track latest request
    setLoading(true);
    const userName = `${user.first_name} ${user.last_name}`;

    try {
      await moveGroupMemberViaAdmin(
        orgCode,
        newGroup,
        false,
        user.auth_id,
        orgAzureId,
      );
      if (latestSelectionRef.current === newGroup) {
        showSuccessToast(
          t(SuccessStringKeys.SUCCESSFULLY_MOVED_USER_TO_GROUP, {
            user: userName,
            group: newGroup,
          }),
        );
        userToHighlight(user.auth_id);
      }
    } catch {
      if (latestSelectionRef.current === newGroup) {
        setSelectedGroup(currentGroup);
      }
      showErrorToast(
        t(ErrorStringKeys.FAILED_TO_UPDATE_USERS_GROUP, {
          user: userName,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle selection change for group selector.
   * @param {import('@mui/material').SelectChangeEvent<string>} e
   * @returns {void}
   */
  const handleSelectChange = (e) => {
    const newGroup = e?.target?.value;

    if (newGroup === '') {
      showErrorToast(t(StringKeys.PLEASE_SELECT_A_GROUP));
      return;
    }

    setSelectedGroup(newGroup); // optimistic update
    updateUserGroup(newGroup);
  };

  return (
    <>
      <FormControl variant="outlined" sx={{ width: '80%', minWidth: 120 }}>
        <Select
          value={selectedGroup || ''}
          onChange={handleSelectChange}
          disabled={loading}
          displayEmpty
          sx={{
            backgroundColor: palette.grey[300],
            border: 'none',
            borderRadius: 2,
            height: 40,
            fontSize,
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: `solid 2px ${COLORS.BLUE_CRAYOLA}`,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        >
          <MenuItem value="" disabled sx={{ fontSize }}>
            {t(StringKeys.SELECT_A_GROUP)}
          </MenuItem>

          {allGroups.map((localGroup) => (
            <MenuItem
              key={localGroup.group_name}
              value={localGroup.group_name}
              disabled={localGroup.group_name === selectedGroup} //  disable current group
              sx={{ fontSize }}
            >
              {localGroup?.group_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <LoadingData show={loading} />
    </>
  );
}

export default GroupSelector;
